import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input, LoadingSpinner } from '../components';
import { authService } from '../services';

/**
 * ResetPasswordPage
 * Design: Black background, white form, minimalist
 * Purpose: Reset password using token from email
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear specific error when user types
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    if (generalError) setGeneralError('');
  };

  const validateForm = () => {
    const newErrors = {};

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError('');

    if (!validateForm()) {
      return;
    }

    if (!token) {
      setGeneralError('Invalid or missing reset token. Please request a new password reset.');
      return;
    }

    setIsLoading(true);

    try {
      const result = await authService.resetPassword(token, formData.password);

      if (result.success) {
        setIsSuccess(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      } else {
        setGeneralError(result.error || 'Failed to reset password. Please try again.');
      }
    } catch (err) {
      setGeneralError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Invalid token state
  if (!token) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-md">
          <div className="bg-white p-8 md:p-12 text-center">
            {/* Error icon */}
            <div className="w-16 h-16 mx-auto mb-6 border-4 border-red-600 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>

            <h2 className="font-mono text-xl font-bold uppercase tracking-wider text-black mb-4">
              Invalid Link
            </h2>
            <p className="font-mono text-sm text-gray-600 mb-8">
              This password reset link is invalid or has expired.
            </p>

            <Link
              to="/forgot-password"
              className="inline-block px-6 py-3 bg-black text-white font-mono text-sm font-bold uppercase tracking-wider border-2 border-black hover:bg-white hover:text-black transition-colors"
            >
              Request New Link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 md:p-8">
      {/* Back to login */}
      <Link
        to="/login"
        className="absolute top-8 left-8 font-mono text-sm uppercase tracking-wider text-gray-400 hover:text-white transition-colors"
      >
        &larr; Back to Login
      </Link>

      {/* Form container */}
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-mono text-3xl md:text-4xl font-bold uppercase tracking-wider text-white mb-4">
            New Credentials
          </h1>
          <p className="font-mono text-sm uppercase tracking-wider text-gray-400">
            Create a new password
          </p>
        </div>

        {/* Success state */}
        {isSuccess ? (
          <div className="bg-white p-8 md:p-12">
            <div className="text-center">
              {/* Success icon */}
              <div className="w-16 h-16 mx-auto mb-6 border-4 border-black flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              <h2 className="font-mono text-xl font-bold uppercase tracking-wider text-black mb-4">
                Password Reset
              </h2>
              <p className="font-mono text-sm text-gray-600 mb-8">
                Your password has been successfully reset. Redirecting to login...
              </p>

              <LoadingSpinner size="sm" />
            </div>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12">
            {/* Error message */}
            {generalError && (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-600">
                <p className="font-mono text-sm text-red-600 uppercase tracking-wider">
                  {generalError}
                </p>
              </div>
            )}

            {/* New password field */}
            <div className="mb-6">
              <Input
                label="New Password"
                id="password"
                name="password"
                type="password"
                placeholder="Min. 8 characters"
                value={formData.password}
                onChange={handleChange}
                error={errors.password}
                required
                disabled={isLoading}
              />
            </div>

            {/* Confirm password field */}
            <div className="mb-8">
              <Input
                label="Confirm Password"
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="Re-enter your password"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={errors.confirmPassword}
                required
                disabled={isLoading}
              />
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingSpinner size="sm" />
                  Resetting...
                </span>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>
        )}

        {/* Login link */}
        <div className="mt-8 text-center">
          <p className="font-mono text-sm uppercase tracking-wider text-gray-400">
            Remember your password?{' '}
            <Link
              to="/login"
              className="text-white hover:underline"
            >
              Login
            </Link>
          </p>
        </div>
      </div>

      {/* Decorative corners */}
      <div className="absolute top-8 right-8 w-16 h-16 border-r-2 border-t-2 border-white opacity-20" />
      <div className="absolute bottom-8 left-8 w-16 h-16 border-l-2 border-b-2 border-white opacity-20" />
      <div className="absolute bottom-8 right-8 w-16 h-16 border-r-2 border-b-2 border-white opacity-20" />
    </div>
  );
}
